import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  useRoutePlanFeedbackTriggerSample,
  useRoutePlanFeedbackTriggerSampleStatus,
} from 'api/route-plan-feedback/route-plan-feedback';
import Button from 'components/inputs/Button';
import Input from 'components/inputs/Input';
import LabelWrap from 'components/inputs/LabelWrap';
import { toInputDateString } from 'utils/date-helpers';
import {
  buildFeedbackPageUrl,
  formatPlanningDate,
} from 'pages/TransportLogistics/Ruttfeedback/feedback-round-helpers';

const Wrapper = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 900px;
`;

const Intro = styled.p`
  margin: 0;
  max-width: 75ch;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const Row = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

const Note = styled.p`
  font-size: 12px;
  color: #666;
  margin: 0;
  max-width: 75ch;
`;

const Result = styled.div`
  padding: 12px 14px;
  border-radius: 8px;
  background: rgb(234, 244, 238);
  border: 1px solid rgb(180, 215, 195);
  max-width: 75ch;
`;

const Failure = styled(Result)`
  background: rgb(251, 236, 236);
  border-color: rgb(230, 180, 180);
`;

const tomorrow = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return toInputDateString(date);
};

/**
 * Kör ruttprovet nu i stället för att vänta på 14:00- eller 17:00-jobbet. Samma körning:
 * ruttplaneringen för dagen, sparad som DailySample, och ett typiskt och ett osäkert paket utvalda.
 * Enbart förhandsvisning — ingenting verkställs. Provet läggs till dagens tidigare prov och blir det
 * som delas ut från och med nu. Se route-planning.md, "Kvällens ruttprov".
 *
 * Planeringen tar en dryg minut och körs i bakgrunden: knappen startar den och sidan frågar efter
 * läget var tredje sekund tills den är klar. Det går därför att lämna sidan och komma tillbaka.
 */
const RoutePlanFeedbackSamplePage: FC = () => {
  const trigger = useRoutePlanFeedbackTriggerSample();
  const [date, setDate] = useState<string>(tomorrow());
  const status = useRoutePlanFeedbackTriggerSampleStatus(
    { date },
    {
      query: {
        enabled: !!date,
        refetchInterval: (data) => (data?.state === 'Running' ? 3000 : false),
      },
    }
  );
  const state = status.data?.state;
  const running = trigger.isLoading || state === 'Running';
  const result = state === 'Done' ? status.data?.result : undefined;

  const run = async () => {
    try {
      await trigger.mutateAsync({
        data: { planningDateString: date || undefined },
      });
    } catch {
      // Felet visas av adminens globala felhantering (MutationCache.onError). Backend svarar med
      // en titel som förklarar: provet har redan svar eller delats ut, eller en körning pågår (409).
    }
    await status.refetch();
  };

  return (
    <Wrapper>
      <Intro>
        Kör ruttprovet nu i stället för att vänta på 14:00 eller 17:00.
        Ruttplaneringen tar fram förslaget för dagen och väljer ut paketen som
        trafikledarna får frågan om i toasten och på sidan Ruttfeedback.
        Ingenting planeras: inga uppdrag tilldelas, låses eller ändras.
      </Intro>

      <Row>
        <LabelWrap label="Planeringsdag">
          <Input
            min={toInputDateString(new Date())}
            onChange={(e) => setDate(e.target.value)}
            type="date"
            value={date}
            width={150}
          />
        </LabelWrap>
        <Button disabled={!date || running} onClick={run}>
          {running ? 'Planerar …' : 'Kör ruttprovet nu'}
        </Button>
      </Row>
      <Note>
        Körningen tar ungefär en minut och kostar Google-anrop som en vanlig
        planering. Den körs i bakgrunden, så du kan lämna sidan och komma
        tillbaka. Provet läggs till dagens tidigare prov och blir det som
        toasten och sidan frågar om från och med nu; tidigare svar ligger kvar.
        Toasten visas bara under rundorna (10:05–11:45, 14:05–15:45 och
        17:05–18:30 för morgondagen, 07:30–09:00 för dagen), för alla trafikledare utom de som undantagits, högst en
        gång per person och halvtimme, och inte längre för den som svarat.
      </Note>

      {state === 'Failed' && (
        <Failure>
          Ruttprovet för {formatPlanningDate(status.data!.planningDateString)}{' '}
          misslyckades: {status.data?.error}
        </Failure>
      )}

      {result && (
        <Result>
          Prov för {formatPlanningDate(result.planningDateString)} klart:
          körning {result.routePlanRunId}, {result.packageCount} paket. Utvalda:{' '}
          {result.sampledPackageIndexes.length > 0
            ? result.sampledPackageIndexes.map((i) => `#${i}`).join(', ')
            : 'inga (inget automatiskt paket att visa)'}
          .{' '}
          <Link to={buildFeedbackPageUrl(result.planningDateString)}>
            Öppna i Ruttfeedback
          </Link>
        </Result>
      )}
    </Wrapper>
  );
};

export default RoutePlanFeedbackSamplePage;
